/**
 * useSandboxSphere
 *
 * State for the composer's *Create as sandbox sphere* affordance
 * (`type-composer.jsx:1841`, `:2099`) — EAINT-897 Phase 5.
 *
 * The affordance is a consequential, outward action (it creates a real sphere),
 * so the states it can be in are explicit rather than implied by a spinner: the
 * caller can always say what happened and what it can do next. A refusal (403)
 * is a first-class state carrying the reason as visible text, not a swallowed
 * error — a control that cannot act must state why (`/standards/frontend/ux-craft.md` I2).
 */

import React from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import { confirmDialogAtom } from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import {
  createSandboxSphere,
  deleteSandboxSphere,
} from '../../../api/sandboxSphere';

export type SandboxSphereStatus =
  | 'idle'
  | 'unavailable'
  | 'creating'
  | 'created'
  | 'deleting'
  | 'refused'
  | 'failed';

export interface SandboxSphereState {
  status: SandboxSphereStatus;
  /** Present from `created` onwards — the sphere the link and the delete act on. */
  sphereId?: string;
  /** The name the sandbox sphere was created under. */
  name?: string;
}

export interface UseSandboxSphereParams {
  graphClient: ApiClient | null | undefined;
  /** The type under composition. `null` while it has not resolved. */
  schemaSystemName: string | null | undefined;
  /** The organization sphere the sandbox is centered on. */
  centerSphereId: string | null | undefined;
  /** The type under composition, for `sandbox_sphere_created`. Analytics only. */
  schemaId?: string | null;
  /** What to call the sandbox sphere — the sample record's own name. */
  sphereName: string;
}

export interface UseSandboxSphereResult {
  state: SandboxSphereState;
  create: () => void;
  remove: () => void;
}

/**
 * `unavailable` (rather than a button that fails on click) is the honest state
 * when there is no client or no saved type to be an instance of: the agent-
 * unavailable posture, applied to a network-backed action.
 */
export function useSandboxSphere({
  graphClient,
  schemaSystemName,
  centerSphereId,
  schemaId,
  sphereName,
}: UseSandboxSphereParams): UseSandboxSphereResult {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const ready = Boolean(graphClient && schemaSystemName && centerSphereId);
  const [state, setState] = React.useState<SandboxSphereState>({ status: 'idle' });
  /** Guards a `setState` after the rail unmounts mid-request. */
  const mounted = React.useRef(true);
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const create = React.useCallback(() => {
    if (!graphClient || !schemaSystemName || !centerSphereId) return;
    setState({ status: 'creating', name: sphereName });
    createSandboxSphere(graphClient, {
      schemaSystemName,
      centerSphereId,
      name: sphereName,
    })
      .then((result) => {
        if (result.outcome === 'created') {
          // Spec § Analytics: `sandbox_sphere_created`, on the 2xx — outside the mounted
          // guard, because the sphere exists whether or not the rail is still on screen,
          // and an unmount must not lose the record of a real sphere being created.
          logAnalyticsEvent('sandbox_sphere_created', undefined, {
            schemaId,
            schemaSystemName,
            sphereId: result.sphereId,
          });
        }
        if (!mounted.current) return;
        setState(
          result.outcome === 'created'
            ? { status: 'created', sphereId: result.sphereId, name: sphereName }
            : { status: 'refused' }
        );
      })
      .catch(() => {
        if (mounted.current) setState({ status: 'failed' });
      });
  }, [graphClient, schemaSystemName, centerSphereId, sphereName, schemaId, logAnalyticsEvent]);

  const remove = React.useCallback(() => {
    const sphereId = state.sphereId;
    if (!graphClient || !sphereId) return;
    const sphereName = state.name ?? '';
    const run = () => {
      setState({ status: 'deleting', sphereId, name: state.name });
      deleteSandboxSphere(graphClient, sphereId)
        .then((result) => {
          if (!mounted.current) return;
          setState(
            result.outcome === 'deleted'
              ? { status: 'idle' }
              : { status: 'refused', sphereId, name: state.name }
          );
        })
        .catch(() => {
          if (mounted.current) setState({ status: 'failed', sphereId, name: state.name });
        });
    };
    // Deleting a real sphere is not undoable, so it states what it will delete and
    // names it (`/standards/frontend/ux-craft.md` I4; spec § UI States: "sandbox sphere
    // delete → confirm naming the sphere"). The confirm lives here rather than in the
    // two panels that render the button, so neither can render the un-confirmed path.
    confirmDialogAtom({
      variant: 'danger',
      header: t('studio.typeEditor.sampleRecord.sandboxDeleteConfirmTitle'),
      message: t('studio.typeEditor.sampleRecord.sandboxDeleteConfirmMessage', { name: sphereName }),
      acceptLabel: t('studio.typeEditor.sampleRecord.sandboxDeleteConfirmAccept'),
      rejectLabel: t('common.cancel'),
      accept: run,
    });
  }, [graphClient, state.sphereId, state.name, t]);

  return {
    state: ready ? state : { status: 'unavailable' },
    create,
    remove,
  };
}

export default useSandboxSphere;
